import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Gift, Package, Wrench, ShoppingBag, CalendarCheck } from "@phosphor-icons/react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { OrderModal } from "@/components/site/OrderModal";
import { STORE, BUSINESS } from "@/lib/data";

export default function Store() {
  const [orderProduct, setOrderProduct] = useState(null);
  const openOrder = (product) => setOrderProduct(product);

  return (
    <div data-testid="store-page">
      <div className="bg-navy-900 pt-28 md:pt-32 -mb-8">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Link to="/supply" data-testid="gear-back-link" className="inline-flex items-center gap-2 text-sm text-navy-300 hover:text-white"><ArrowLeft size={16} /> Back to AMWAP Supply</Link>
        </div>
      </div>
      <PageHero
        testId="store-hero"
        eyebrow="AMWAP Supply · Gear & Services"
        lines={["Packages,", "gifts & gear."]}
        subtitle="Service packages, gift cards and AMWAP merch. Place an order online and we'll email you to arrange payment and pickup or delivery."
      />

      {/* Notice */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex flex-wrap items-center justify-center gap-2 text-sm text-slate-600">
          <span className="font-semibold text-navy-900">No online payment needed.</span>
          Place an order and we'll confirm the details, or call{" "}
          <a href={BUSINESS.phoneHref} className="font-bold text-navy-600 underline underline-offset-2">
            {BUSINESS.phone}
          </a>
        </div>
      </div>

      {/* Packages */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <Wrench size={22} weight="fill" className="text-navy-600" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">Service Packages</span>
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy-900 leading-[1.1] max-w-2xl">
            Book a block of trusted help.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {STORE.packages.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06}>
              <div
                data-testid={`store-package-${i}`}
                className="relative bg-white border border-slate-200 rounded-2xl p-7 h-full flex flex-col hover:shadow-[0_12px_40px_rgb(10,17,40,0.08)] hover:-translate-y-1 transition-all"
              >
                {p.tag && (
                  <span className="absolute top-5 right-5 rounded-full bg-navy-900 text-white text-xs font-bold px-3 py-1">
                    {p.tag}
                  </span>
                )}
                <h3 className="font-display font-bold text-xl text-navy-900 tracking-tight pr-16">{p.name}</h3>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className="font-display font-black text-4xl text-navy-900">{p.price}</span>
                  <span className="text-sm text-slate-500">/ {p.unit}</span>
                </div>
                <p className="text-slate-600 mt-3 flex-1">{p.desc}</p>
                <button
                  data-testid={`order-open-package-${i}`}
                  onClick={() => openOrder({ name: p.name, price: `${p.price} / ${p.unit}`, category: "Service Package" })}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-3 font-bold hover:bg-navy-600 transition-colors"
                >
                  <CalendarCheck weight="fill" size={16} /> Book this Service
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Gift cards */}
      <section className="bg-navy-900 text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <Gift size={22} weight="fill" className="text-navy-400" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-400">Gift Cards</span>
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.1] max-w-2xl">
              The gift of a to-do list, done.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {STORE.giftcards.map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  data-testid={`store-giftcard-${i}`}
                  className="relative rounded-2xl p-8 h-full border border-white/15 bg-gradient-to-br from-navy-700 to-navy-900 overflow-hidden group flex flex-col"
                >
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5 group-hover:scale-125 transition-transform duration-700" />
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-navy-400">AMWAP Gift Card</span>
                  <div className="font-display font-black text-6xl mt-4">{g.price}</div>
                  <p className="text-slate-300 mt-4 flex-1">{g.desc}</p>
                  <button
                    data-testid={`order-open-giftcard-${i}`}
                    onClick={() => openOrder({ name: `Gift Card ${g.price}`, price: g.price, category: "Gift Card" })}
                    className="relative mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy-900 py-3 font-bold hover:bg-slate-100 transition-colors"
                  >
                    <ShoppingBag weight="fill" size={16} /> Place an Order
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Merch */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <Package size={22} weight="fill" className="text-navy-600" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">Merch & Gear</span>
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy-900 leading-[1.1] max-w-2xl">
            Rep the brand.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {STORE.merch.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.08}>
              <div
                data-testid={`store-merch-${i}`}
                className="group bg-white border border-slate-200 rounded-2xl overflow-hidden h-full flex flex-col hover:shadow-[0_12px_40px_rgb(10,17,40,0.08)] transition-shadow"
              >
                <div className="relative overflow-hidden bg-slate-100">
                  <img src={m.image} alt={m.name} className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-lg text-navy-900">{m.name}</h3>
                    <span className="font-display font-black text-xl text-navy-900">{m.price}</span>
                  </div>
                  <p className="text-slate-600 mt-2 flex-1">{m.desc}</p>
                  <button
                    data-testid={`order-open-merch-${i}`}
                    onClick={() => openOrder({ name: m.name, price: m.price, category: "Merch" })}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-3 font-bold hover:bg-navy-600 transition-colors"
                  >
                    <ShoppingBag weight="fill" size={16} /> Place an Order
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {orderProduct && <OrderModal product={orderProduct} onClose={() => setOrderProduct(null)} />}
    </div>
  );
}
